import React from 'react';
import { Formik } from 'formik';
import { Divider, Spin, Card } from 'antd';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { Breadcrumb } from '../../components/Card/Cards';
import PrintTemplate from '../../components/Print/PrintTemplate';
import PatientCCPaperView from '../../components/PatientCCPaperView/PatientCCPaperView';
import { formSections } from './formConfig';
import { InpatientAssessmentValidationSchema } from '../../utils/FormValidation';
import { useInpatientAssessment } from './hooks/useInpatientAssessment';
import { AssessmentFormHeader } from './components/AssessmentFormHeader';
import { AssessmentField } from './components/AssessmentFormFields';
import { AssessmentFormActions } from './components/AssessmentFormActions';

/**
 * In Patient Initial Medical Assessment Form (Adult)
 * Refactored modular version
 */
const InpatientAssessmentForm = () => {
    const {
        loading,
        isEditMode,
        toggleEditMode,
        initialValues,
        handleSubmit,
        patientId,
        roomName,
        roomType
    } = useInpatientAssessment();


    const BreadcrumbList = [
        {
            name: 'In Patient Initial Medical Assessment Form (Adult)',
            linkTo: '',
        },
    ];

    const printSections = React.useMemo(() => {
        const patientSummarySection = {
            sectionIndex: 1,
            sectionTitle: 'Patient Summary',
            layout: 'grid-2',
            fields: [
                { fieldKey: 'patientName', label: 'Patient Name', gridClass: 'col-6' },
                { fieldKey: 'casePaperNo', label: 'Case Paper No', gridClass: 'col-6' },
                { fieldKey: 'age', label: 'Age', gridClass: 'col-6' },
                { fieldKey: 'gender', label: 'Gender', gridClass: 'col-6' },
                { fieldKey: 'roomName', label: 'Room Name', gridClass: 'col-6' },
                { fieldKey: 'roomType', label: 'Room Type', gridClass: 'col-6' },
                { fieldKey: 'consultantName', label: 'Consultant Name', gridClass: 'col-6' },
            ],
        };

        const clinicalSections = formSections
            .filter((section) => !section.hideInUI)
            .map((section, index) => ({
                ...section,
                sectionIndex: index + 2,
                layout: 'grid-2',
                fields: section.fields.map((field) => ({
                    ...field,
                    gridClass:
                        field.fieldKey === 'chiefComplaint' || section.fields.length === 1
                            ? 'col-12'
                            : 'col-6',
                })),
            }));

        return [patientSummarySection, ...clinicalSections];
    }, []);

    if (loading) {
        return (
            <div className="main-wrapper">
                <Header />
                <Sidebar />
                <div className="page-wrapper">
                    <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                        <Spin size="large" tip="Loading assessment data..." />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main-wrapper">
            <Header />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content">
                    <style>
                        {`
                            @media print {
                                @page {
                                    size: A4;
                                    margin: 10mm;
                                }

                                #printable-area.print-template {
                                    font-size: 11px !important;
                                    line-height: 1.3 !important;
                                    color: #111827 !important;
                                    font-family: 'Open Sans', Arial, sans-serif !important;
                                }

                                #printable-area .print-header {
                                    margin-bottom: 8px !important;
                                    padding-bottom: 6px !important;
                                    border-bottom: 1px solid #111827 !important;
                                }

                                #printable-area .print-logo img {
                                    max-height: 58px !important;
                                    filter: none !important;
                                    -webkit-print-color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                }

                                #printable-area img {
                                    filter: none !important;
                                    -webkit-print-color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                }

                                #printable-area .print-hospital-name {
                                    color: #111827 !important;
                                    font-size: 16px !important;
                                    font-weight: 700 !important;
                                    margin: 0 0 2px !important;
                                }

                                #printable-area .print-subtitle {
                                    color: #4b5563 !important;
                                    font-size: 10px !important;
                                    margin: 0 0 2px !important;
                                }

                                #printable-area .print-contact-info {
                                    font-size: 9px !important;
                                    gap: 8px !important;
                                }

                                #printable-area .contact-icon {
                                    display: none !important;
                                }

                                #printable-area .print-divider {
                                    height: 1px !important;
                                    margin: 6px 0 !important;
                                    background: #111827 !important;
                                }

                                #printable-area .print-form-title {
                                    margin-bottom: 10px !important;
                                    padding: 8px 10px !important;
                                    border: 1px solid #111827 !important;
                                    background: #f5f5f5 !important;
                                }

                                #printable-area .print-form-title h2 {
                                    margin: 0 !important;
                                    font-size: 14px !important;
                                    color: #111827 !important;
                                    font-weight: 700 !important;
                                    letter-spacing: 0.2px !important;
                                }

                                #printable-area .print-date {
                                    margin: 2px 0 0 !important;
                                    font-size: 9px !important;
                                    color: #4b5563 !important;
                                }

                                #printable-area .print-section {
                                    margin-bottom: 10px !important;
                                    border: 1px solid #111827 !important;
                                    break-inside: avoid !important;
                                }

                                #printable-area .print-section-header {
                                    background: #f3f4f6 !important;
                                    padding: 6px 8px !important;
                                    border-bottom: 1px solid #111827 !important;
                                    margin-bottom: 0 !important;
                                }

                                #printable-area .print-section-header h3 {
                                    color: #111827 !important;
                                    font-size: 11px !important;
                                    margin: 0 !important;
                                    font-weight: 700 !important;
                                }

                                #printable-area .print-section-description {
                                    color: #4b5563 !important;
                                    font-size: 9px !important;
                                    font-weight: 400 !important;
                                    margin: 1px 0 0 !important;
                                    font-style: normal !important;
                                }

                                #printable-area .print-section-content {
                                    padding: 8px !important;
                                }

                                #printable-area .print-section-content.row {
                                    margin: 0 !important;
                                    display: flex !important;
                                    flex-wrap: wrap !important;
                                }

                                #printable-area .print-section-content > .print-field {
                                    padding-left: 6px !important;
                                    padding-right: 6px !important;
                                    margin-bottom: 8px !important;
                                }

                                #printable-area .col-6 {
                                    flex: 0 0 50% !important;
                                    max-width: 50% !important;
                                }

                                #printable-area .col-12 {
                                    flex: 0 0 100% !important;
                                    max-width: 100% !important;
                                }

                                #printable-area .print-field-label {
                                    display: block !important;
                                    margin: 0 0 3px !important;
                                    font-size: 9.5px !important;
                                    font-weight: 600 !important;
                                    color: #374151 !important;
                                    text-transform: none !important;
                                    letter-spacing: 0 !important;
                                }

                                #printable-area .print-field-value {
                                    display: block !important;
                                    padding: 6px 8px !important;
                                    font-size: 10.5px !important;
                                    color: #111827 !important;
                                    border: 1px solid #d1d5db !important;
                                    border-radius: 3px !important;
                                    background: #fff !important;
                                    white-space: pre-wrap !important;
                                    word-break: break-word !important;
                                    min-height: 24px !important;
                                }

                                #printable-area .print-footer {
                                    margin-top: 14px !important;
                                    padding-top: 8px !important;
                                    border-top: 1px solid #111827 !important;
                                }

                                #printable-area .print-signature-section {
                                    margin-top: 12px !important;
                                    gap: 12px !important;
                                    display: flex !important;
                                    flex-wrap: nowrap !important;
                                    justify-content: space-between !important;
                                    align-items: flex-end !important;
                                }

                                #printable-area .print-signature-box {
                                    flex: 1 1 0 !important;
                                    min-width: 0 !important;
                                }

                                #printable-area .print-signature-line {
                                    height: 30px !important;
                                    border-bottom: 1px solid #111827 !important;
                                }

                                #printable-area .print-signature-box p {
                                    font-size: 9px !important;
                                    color: #374151 !important;
                                }

                                #printable-area .print-footer-branding p {
                                    font-size: 8.5px !important;
                                    color: #6b7280 !important;
                                }
                            }

                            @media print and (max-width: 210mm) {
                                #printable-area .col-6 {
                                    flex: 0 0 50% !important;
                                    max-width: 50% !important;
                                }

                                #printable-area .print-signature-section {
                                    flex-direction: row !important;
                                    flex-wrap: nowrap !important;
                                }

                                #printable-area .print-signature-box {
                                    min-width: 0 !important;
                                }
                            }
                        `}
                    </style>
                    <Breadcrumb page={BreadcrumbList} />
                    <Card
                        className="shadow-sm border-0"
                        style={{ borderRadius: '12px', overflow: 'hidden' }}
                        bodyStyle={{ padding: '30px' }}
                    >
                        <AssessmentFormHeader />
                        <Divider />
                        <Formik
                            enableReinitialize
                            initialValues={initialValues}
                            validationSchema={InpatientAssessmentValidationSchema}
                            onSubmit={handleSubmit}
                        >
                            {(formikProps) => {
                                const { values, handleSubmit: handleFormikSubmit, isSubmitting, dirty } = formikProps;

                                return (
                                    <>
                                        {/* Form - Hidden in print */}
                                        <form onSubmit={handleFormikSubmit} className="no-print">
                                            <PatientCCPaperView userId={patientId} extraInfo={{ "Room Name": roomName, "Room Type": roomType }} />

                                            {formSections.filter(s => !s.hideInUI).map((section) => (
                                                <div key={section.sectionIndex} className="mb-5">
                                                    <Divider orientation="left">
                                                        <h4 className="text-primary m-0" style={{ fontWeight: '600' }}>
                                                            {section.sectionTitle}
                                                        </h4>
                                                    </Divider>

                                                    <div className="row">
                                                        {section.fields.map((field) => (
                                                            <React.Fragment key={`${section.sectionIndex}-${field.fieldKey}`}>
                                                                <AssessmentField
                                                                    field={field}
                                                                    {...formikProps}
                                                                    isEditMode={isEditMode}
                                                                />
                                                            </React.Fragment>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}

                                            <AssessmentFormActions
                                                isSubmitting={isSubmitting}
                                                isEditMode={isEditMode}
                                                onToggleEdit={toggleEditMode}
                                            />
                                        </form>

                                        {/* Print Template - Hidden on screen, visible in print */}
                                        {/* Print Template - Hidden on screen, visible in print */}
                                        <div id="inpatient-print-area" style={{ display: 'none' }} className="print-only">
                                            <PrintTemplate
                                                formData={values}
                                                sections={printSections}
                                                formTitle="In Patient Initial Medical Assessment Form (Adult)"
                                                showBlankPage={false}
                                            />
                                        </div>
                                    </>
                                );
                            }}
                        </Formik>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default InpatientAssessmentForm;
